import { useCallback, useState } from 'react';
import { JobMasterKey } from './masters.config';
import { MasterRow } from './MasterFormDialog';
import { FilterOption } from './workJobReport.api';

/**
 * Master rows added or renamed through a "+ Add" / pencil popup, held until the page's
 * own option refetch lands.
 *
 * Lives with the form rather than inside each picker because the save resolves four of
 * the lookups (locality and the three work types) back to a *label* — a row that only the
 * picker knew about would resolve to nothing and silently drop out of the payload.
 */
export function useAddedMasterRows() {
  const [added, setAdded] = useState<Partial<Record<JobMasterKey, MasterRow[]>>>({});

  /** The option list plus anything added here, newest label winning, sorted for display. */
  const withAdded = useCallback(
    (list: FilterOption[], masterKey?: JobMasterKey): FilterOption[] => {
      const rows = masterKey ? added[masterKey] : undefined;
      if (!rows?.length) return list;

      const byId = new Map<number, FilterOption>();
      list.forEach((option) => byId.set(option.id, option));
      rows.forEach((row) => byId.set(row.id, { id: row.id, label: row.label }));
      return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
    },
    [added]
  );

  const remember = useCallback((masterKey: JobMasterKey, row: MasterRow) => {
    setAdded((prev) => ({
      ...prev,
      [masterKey]: [...(prev[masterKey] ?? []).filter((r) => r.id !== row.id), row],
    }));
  }, []);

  const clear = useCallback(() => setAdded({}), []);

  return { withAdded, remember, clear };
}
